import { type ReactElement, type ReactNode } from 'react';
import { useHotkeys } from 'react-hotkeys-hook';

import { Button, Flex, IconFilter, Text } from '@spotify-internal/encore-web';

import { useFilterShortcut } from '../../hooks/useFilterShortcut';
import { FilterSearch } from './FilterSearch';
import { InlineFilterTrigger } from './InlineFilterTrigger';
import { type FilterDefinition, useFilters } from './provider';
import { useFilterPanel } from './useFilterPanel';

import styles from './FilterBar.module.css';

type FilterControlsProps = {
  children?: ReactNode;
  filters?: FilterDefinition[];
  searchCollapsed?: boolean;
  hideSearch?: boolean;
  debounceTime?: number;
};

export const FilterControls = ({
  children,
  filters: filtersOverride,
  searchCollapsed = false,
  hideSearch = false,
  debounceTime = 300,
}: FilterControlsProps): ReactElement => {
  const { filters, activeFilters, isOpen, toggle, panelId, triggerRef } =
    useFilterPanel({ filters: filtersOverride });
  const activeFilterCount = activeFilters.length;
  const [shortcutEnabled] = useFilterShortcut();
  useHotkeys(
    'meta+shift+f',
    event => {
      event.preventDefault();
      triggerRef.current?.focus();
      toggle();
    },
    { enabled: shortcutEnabled && filters.length > 0, enableOnFormTags: true },
    [toggle, filters.length],
  );

  return (
    <>
      {!hideSearch && (
        <FilterSearch
          searchCollapsed={searchCollapsed}
          debounceTime={debounceTime}
        />
      )}
      {children}
      {filters.length > 0 && (
        <Button
          ref={triggerRef}
          className={styles.disclosure}
          type="button"
          variant="tertiary"
          size="small"
          aria-expanded={isOpen}
          aria-controls={panelId}
          onClick={toggle}
         data-track-source-anchor="907e13544c;filter-controls-button">
          <IconFilter size="small" aria-hidden />
          Filters{activeFilterCount > 0 ? ` (${activeFilterCount})` : ''}
        </Button>
      )}
    </>
  );
};

export const FilterPanel = ({
  filters: filtersOverride,
}: {
  filters?: FilterDefinition[];
}): ReactElement | null => {
  const { activeFilters, setFilters } = useFilters();
  const {
    filters: panelFilters,
    activeFilters: activePanelFilters,
    filterIds: panelFilterIds,
    isOpen,
    panelId,
    open,
  } = useFilterPanel({ filters: filtersOverride });

  if (!panelFilters.length) return null;

  return (
    <Flex
      id={panelId}
      data-component="FilterPanel"
      role="region"
      aria-label="Filters"
      hidden={!isOpen}
      alignItems="start"
      gap="base"
      className={styles.panel}
    >
      <Flex alignItems="center" minHeight="var(--encore-control-size-smaller)">
        <Text variant="bodySmallBold">Filters</Text>
      </Flex>
      <Flex
        className={styles.filters}
        alignItems="center"
        gap="tighter-2"
        wrap="wrap"
      >
        {isOpen &&
          panelFilters.map(filter => (
            <InlineFilterTrigger key={filter.id} filter={filter} />
          ))}
      </Flex>
      <Button
        className={styles.clearButton}
        type="button"
        variant="tertiary"
        size="small"
        disabled={activePanelFilters.length === 0}
        onClick={() => {
          open();
          setFilters(
            activeFilters
              .filter(filter => !panelFilterIds.has(filter.id))
              .map(filter => ({ filter, value: filter.value })),
          );
        }}
       data-track-source-anchor="76d6d8394b">
        Clear all
      </Button>
    </Flex>
  );
};

export const FilterBar = ({
  children,
  ...props
}: FilterControlsProps): ReactElement => (
  <>
    <Flex className={styles.topContainer} alignItems="center" gap="tighter-2">
      <FilterControls {...props}>{children}</FilterControls>
    </Flex>
    <FilterPanel filters={props.filters} />
  </>
);
